// ============================================================================
// DASHBOARD SKELETON LOADERS
// ============================================================================
// Skeleton components for loading states with shimmer animation

import React from 'react';

// ============================================================================
// STYLES
// ============================================================================

const skeletonStyles = `
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  .skeleton {
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.06) 25%,
      rgba(0, 0, 0, 0.1) 50%,
      rgba(0, 0, 0, 0.06) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
    border-radius: 4px;
  }

  .skeleton-dark {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.06) 25%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0.06) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
    border-radius: 4px;
  }

  .skeleton-pulse {
    animation: skeleton-pulse 1.5s ease-in-out infinite;
  }

  @keyframes skeleton-pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// ============================================================================
// BASE SKELETON COMPONENT
// ============================================================================

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  style?: React.CSSProperties;
  isDarkMode?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '1rem',
  borderRadius = '4px',
  style,
  isDarkMode = false,
}) => (
  <div
    className={isDarkMode ? 'skeleton-dark' : 'skeleton'}
    style={{
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
      borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
      ...style,
    }}
  />
);

// ============================================================================
// STATS BAR SKELETON
// ============================================================================

interface StatsSkeletonProps {
  isDarkMode?: boolean;
  itemCount?: number;
}

export const StatsSkeleton: React.FC<StatsSkeletonProps> = ({
  isDarkMode = false,
  itemCount = 5,
}) => (
  <div
    style={{
      display: 'flex',
      gap: '0.5rem',
      margin: '0.5rem 1.5rem 0.5rem 1.5rem',
      padding: '0.375rem 0.75rem',
      borderRadius: '0.5rem',
      background: isDarkMode ? '#1e293b' : '#f8fafc',
      border: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
    }}
  >
    {Array.from({ length: itemCount }).map((_, index) => (
      <div
        key={index}
        style={{
          textAlign: 'center',
          padding: '0.125rem',
          minWidth: '70px',
          flex: '1 1 auto',
        }}
      >
        <Skeleton
          width={40}
          height={20}
          isDarkMode={isDarkMode}
          style={{ margin: '0 auto' }}
        />
        <Skeleton
          width={50}
          height={10}
          isDarkMode={isDarkMode}
          style={{ margin: '0.25rem auto 0' }}
        />
      </div>
    ))}
  </div>
);

// ============================================================================
// TABLE ROW SKELETON
// ============================================================================

interface TableRowSkeletonProps {
  columnCount?: number;
  isDarkMode?: boolean;
}

export const TableRowSkeleton: React.FC<TableRowSkeletonProps> = ({
  columnCount = 8,
  isDarkMode = false,
}) => (
  <tr>
    {Array.from({ length: columnCount }).map((_, index) => (
      <td key={index} style={{ padding: '0.75rem 0.5rem' }}>
        <Skeleton
          width={index === 0 ? '80%' : index === 1 ? '90%' : '70%'}
          height={16}
          isDarkMode={isDarkMode}
        />
      </td>
    ))}
  </tr>
);

// ============================================================================
// TABLE SKELETON (Multiple Rows)
// ============================================================================

interface TableSkeletonProps {
  rowCount?: number;
  columnCount?: number;
  isDarkMode?: boolean;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rowCount = 8,
  columnCount = 8,
  isDarkMode = false,
}) => (
  <>
    {Array.from({ length: rowCount }).map((_, index) => (
      <TableRowSkeleton
        key={index}
        columnCount={columnCount}
        isDarkMode={isDarkMode}
      />
    ))}
  </>
);

// ============================================================================
// GRID CARD SKELETON (for grid view)
// ============================================================================

interface GridCardSkeletonProps {
  isDarkMode?: boolean;
}

export const GridCardSkeleton: React.FC<GridCardSkeletonProps> = ({
  isDarkMode = false,
}) => (
  <div
    style={{
      background: isDarkMode ? '#1e293b' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
      borderRadius: '0.5rem',
      padding: '1rem',
      minWidth: '280px',
    }}
  >
    {/* Header */}
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
      <Skeleton width={100} height={16} isDarkMode={isDarkMode} />
      <Skeleton width={60} height={20} borderRadius={12} isDarkMode={isDarkMode} />
    </div>
    {/* Title */}
    <Skeleton width="90%" height={18} isDarkMode={isDarkMode} style={{ marginBottom: '0.5rem' }} />
    {/* Details */}
    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem' }}>
      <Skeleton width={80} height={14} isDarkMode={isDarkMode} />
      <Skeleton width={100} height={14} isDarkMode={isDarkMode} />
    </div>
    {/* Footer */}
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
      <Skeleton width={70} height={24} borderRadius={4} isDarkMode={isDarkMode} />
      <Skeleton width={80} height={24} borderRadius={4} isDarkMode={isDarkMode} />
    </div>
  </div>
);

// ============================================================================
// GRID VIEW SKELETON
// ============================================================================

interface GridViewSkeletonProps {
  cardCount?: number;
  isDarkMode?: boolean;
}

export const GridViewSkeleton: React.FC<GridViewSkeletonProps> = ({
  cardCount = 6,
  isDarkMode = false,
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1rem',
      padding: '1rem',
    }}
  >
    {Array.from({ length: cardCount }).map((_, index) => (
      <GridCardSkeleton key={index} isDarkMode={isDarkMode} />
    ))}
  </div>
);

// ============================================================================
// LOADING OVERLAY (for refresh operations)
// ============================================================================

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  isDarkMode?: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  message = 'Loading...',
  isDarkMode = false,
}) => {
  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isDarkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        borderRadius: 'inherit',
      }}
    >
      {/* Spinner */}
      <div
        style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      {message && (
        <div
          style={{
            marginTop: '1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: isDarkMode ? '#e2e8f0' : '#64748b',
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// INLINE SPINNER (for buttons or small areas)
// ============================================================================

interface InlineSpinnerProps {
  size?: number;
  color?: string;
}

export const InlineSpinner: React.FC<InlineSpinnerProps> = ({
  size = 16,
  color = '#3b82f6',
}) => (
  <div
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}30`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      display: 'inline-block',
    }}
  />
);

// ============================================================================
// SKELETON STYLES INJECTOR
// ============================================================================

export const SkeletonStyles: React.FC = () => (
  <style dangerouslySetInnerHTML={{ __html: skeletonStyles }} />
);

// ============================================================================
// FULL DASHBOARD SKELETON (Initial Load)
// ============================================================================

interface DashboardSkeletonProps {
  isDarkMode?: boolean;
  viewMode?: 'list' | 'grid';
}

export const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({
  isDarkMode = false,
  viewMode = 'list',
}) => (
  <>
    <SkeletonStyles />
    {/* Stats Skeleton */}
    <StatsSkeleton isDarkMode={isDarkMode} itemCount={5} />

    {/* Content Skeleton */}
    <div style={{ padding: '0 1.5rem' }}>
      {viewMode === 'list' ? (
        <div
          style={{
            background: isDarkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
            borderRadius: '0.5rem',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Request No', 'Subject', 'Type', 'Role', 'Stage', 'Status', 'Priority', 'Actions'].map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'left',
                      background: isDarkMode ? '#334155' : '#f8fafc',
                      borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableSkeleton rowCount={8} columnCount={8} isDarkMode={isDarkMode} />
            </tbody>
          </table>
        </div>
      ) : (
        <GridViewSkeleton cardCount={6} isDarkMode={isDarkMode} />
      )}
    </div>
  </>
);

export default DashboardSkeleton;
